import { apiClient } from './api-client'

export type HelpAudience = 'customer' | 'admin' | 'internal' | 'private_beta'
export type HelpStatus = 'draft' | 'published' | 'archived'

export interface HelpEntry {
  id: string
  type: string
  title: string
  summary: string
  body: string
  moduleSlug: string | null
  featureFlagKey: string | null
  featurePlans: string[]
  audience: HelpAudience
  relatedIds: string[]
  appPaths: string[]
  keywords: string[]
  embeddingHash: string | null
  status: HelpStatus
  isPublic: boolean
  authorId: string | null
  publishedAt: string
  updatedAt: string
  chunkCount?: number
}

export interface HelpEntryWriteInput {
  id?: string
  type: string
  title: string
  summary: string
  body: string
  moduleSlug?: string | null
  featureFlagKey?: string | null
  featurePlans?: string[]
  audience?: HelpAudience
  relatedIds?: string[]
  appPaths?: string[]
  keywords?: string[]
  status?: HelpStatus
  isPublic?: boolean
}

export interface HelpEntryFilters {
  moduleSlug?: string
  type?: string
  status?: HelpStatus | 'all'
  audience?: HelpAudience | 'all'
  visibility?: 'public' | 'gated' | 'all'
  featurePlan?: string
  q?: string
  limit?: number
}

export interface HelpReembedResult {
  id: string
  chunkCount: number
  embeddingHash: string
  skipped?: boolean
}

export interface HelpAnalyticsQueryMetric {
  query: string
  count: number
  avgTopScore: number | null
  lastSeenAt: string
}

export interface HelpAnalyticsRetrievedEntryMetric {
  id: string
  title: string
  moduleSlug: string | null
  featurePlans: string[]
  count: number
}

export interface HelpAnalyticsSummary {
  days: number
  totalQueries: number
  noMatchCount: number
  noMatchRate: number
  articleViews: number
  helpCenterSearches: number
  noResultSearches: number
  helpfulFeedbackCount: number
  unhelpfulFeedbackCount: number
  topQueries: HelpAnalyticsQueryMetric[]
  noMatchQueries: HelpAnalyticsQueryMetric[]
  topRetrievedEntries: HelpAnalyticsRetrievedEntryMetric[]
  topViewedEntries: HelpAnalyticsRetrievedEntryMetric[]
}

export interface HelpSearchResult {
  id: string
  title: string
  summary: string
  body?: string
  type: string
  moduleSlug: string | null
  featureFlagKey: string | null
  featurePlans: string[]
  audience: HelpAudience
  relatedIds: string[]
  appPaths: string[]
  keywords: string[]
  chunk: string
  score: number
  isAvailableOnCurrentPlan: boolean
}

function buildQueryParams(filters: HelpEntryFilters = {}) {
  const params = new URLSearchParams()

  Object.entries(filters).forEach(([key, value]) => {
    if (
      value === undefined ||
      value === null ||
      value === '' ||
      value === 'all'
    ) {
      return
    }
    params.set(key, String(value))
  })

  const query = params.toString()
  return query ? `?${query}` : ''
}

export const helpAdminApi = {
  analytics(
    filters: {
      days?: number
      limit?: number
      scope?: 'global' | 'organization'
    } = {},
  ) {
    const params = new URLSearchParams()
    if (filters.days) params.set('days', String(filters.days))
    if (filters.limit) params.set('limit', String(filters.limit))
    if (filters.scope) params.set('scope', filters.scope)
    const query = params.toString()

    return apiClient<HelpAnalyticsSummary>(
      `/help/admin/analytics/summary${query ? `?${query}` : ''}`,
    )
  },

  list(filters?: HelpEntryFilters) {
    return apiClient<HelpEntry[]>(`/help/admin${buildQueryParams(filters)}`)
  },

  get(id: string) {
    return apiClient<HelpEntry>(`/help/admin/${encodeURIComponent(id)}`)
  },

  create(input: HelpEntryWriteInput) {
    return apiClient<HelpEntry>('/help/admin', {
      method: 'POST',
      body: input,
    })
  },

  update(id: string, input: Partial<HelpEntryWriteInput>) {
    return apiClient<HelpEntry>(`/help/admin/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: input,
    })
  },

  reembed(id: string) {
    return apiClient<HelpReembedResult>(
      `/help/admin/${encodeURIComponent(id)}/reembed`,
      { method: 'POST' },
    )
  },

  delete(id: string) {
    return apiClient<HelpEntry>(`/help/admin/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    })
  },
}

export const helpApi = {
  list(
    filters?: Pick<HelpEntryFilters, 'moduleSlug' | 'type' | 'q' | 'limit'>,
  ) {
    return apiClient<HelpEntry[]>(`/help${buildQueryParams(filters)}`)
  },

  get(id: string) {
    return apiClient<HelpEntry>(`/help/${encodeURIComponent(id)}`)
  },

  categories() {
    return apiClient<
      Array<{ slug: string; count: number; featureFlagKey: string | null }>
    >('/help/categories')
  },

  search(filters: { q: string; moduleSlug?: string; limit?: number }) {
    const params = new URLSearchParams()
    params.set('q', filters.q)
    if (filters.moduleSlug) params.set('moduleSlug', filters.moduleSlug)
    if (filters.limit) params.set('limit', String(filters.limit))

    return apiClient<HelpSearchResult[]>(`/help/search?${params.toString()}`)
  },
}
